/**
 * People — everyone connected to one academy, and what each of them is.
 *
 * Wraps the tenancy app's member endpoints. Membership there is Open edX's own tables
 * and authority is its org roles, so everything below reads and writes platform data;
 * there is no team model to keep in sync.
 *
 *   GET    /api/tenant/v1/tenants/<slug>/members              list
 *   POST   /api/tenant/v1/tenants/<slug>/members              add an existing account
 *   PATCH  /api/tenant/v1/tenants/<slug>/members/<username>   change persona
 *   DELETE /api/tenant/v1/tenants/<slug>/members/<username>   remove from this academy
 *
 * The server is the boundary: it re-checks authority on every call and owns the guards
 * (no self-removal, no removing the last administrator). The persona this file exports
 * only decides what the page offers.
 *
 * ONE LIST, THREE REASONS TO BE ON IT. The list is not "the team" and not "the
 * learners" — it is the union of everyone who registered here, everyone holding a role
 * in this academy's orgs, and everyone actively enrolled in one of its courses. A
 * person is very often two of those at once (an instructor taking a colleague's
 * course), which is exactly why the two used to be one dataset pretending to be two.
 * `sources` says which of the three applies, because an administrator meeting an
 * unfamiliar name needs to know why it is in front of them.
 */

import { openedx, ApiError } from './openedx';

/** The three things a person can be in an academy. `superadmin` is platform-wide. */
export type MemberPersona = 'tenant_admin' | 'tenant_staff' | 'learner';

/** Why a person appears in this academy's list. Any combination is possible. */
export type MemberSource = 'signup' | 'role' | 'enrolment';

export interface MemberEnrolment {
  /** Course key, e.g. `course-v1:ORG+NUMBER+RUN`. */
  courseId: string;
  title: string;
}

export interface Member {
  username: string;
  email: string;
  /** Full name, falling back to the username — the server resolves which. */
  name: string;
  /**
   * What they are in THIS academy.
   *
   * `superadmin` should never arrive on a row: the API leaves platform operators out of
   * every academy's list, because they run the platform rather than belong to an
   * academy, and every write against one is refused anyway. The value stays in the type
   * because a response is not something to trust blindly — a row carrying it renders as
   * an unchangeable "Platform administrator" rather than as a blank cell — and because
   * `personaOf` resolves the VIEWER's own persona to it, which is what decides whether
   * this page offers its controls at all.
   */
  persona: MemberPersona | 'superadmin';
  isActive: boolean;
  /** ISO date. UserSignupSource carries no timestamp, so this is the account's own. */
  joined: string;
  /** Which of the three signals put them on the list, in canonical order. */
  sources: MemberSource[];
  /** Active enrolments in THIS academy's courses. */
  enrolmentCount: number;
  enrolments: MemberEnrolment[];
  /** Courses in this academy where they hold a per-course role, not an org-wide one. */
  staffCourses: string[];
}

/** POST echoes the member back, plus whether this call is what added them. */
export interface AddedMember extends Member {
  created: boolean;
}

const base = (slug: string) => `/api/tenant/v1/tenants/${encodeURIComponent(slug)}/members`;

/**
 * Fill in the fields an older server does not send.
 *
 * The API and this app deploy separately, and a missing array here would be a crash in
 * a table cell rather than a missing badge. Absent is read as "nothing known", never as
 * an empty fact — which is why `sources` renders as "no reason given" rather than as
 * "not connected to this academy".
 */
function normalise<T extends Member>(raw: T): T {
  const enrolments = raw.enrolments ?? [];
  return {
    ...raw,
    sources: raw.sources ?? [],
    enrolments,
    enrolmentCount: raw.enrolmentCount ?? enrolments.length,
    staffCourses: raw.staffCourses ?? [],
  };
}

export const listMembers = async (slug: string): Promise<Member[]> =>
  (await openedx.get<Member[]>(base(slug))).map(normalise);

/**
 * Add an existing account to this academy.
 *
 * `identifier` is an email address or a username; the server decides which by looking
 * for '@'. It will NOT create an account — an unknown address comes back as a 404 with
 * `code: 'no_such_account'`, which is a real answer to show, not an error to swallow.
 */
export const addMember = async (slug: string, identifier: string, persona: MemberPersona) =>
  normalise(
    await openedx.post<AddedMember>(base(slug), {
      [identifier.includes('@') ? 'email' : 'username']: identifier.trim(),
      persona,
    }),
  );

export const setMemberPersona = async (slug: string, username: string, persona: MemberPersona) =>
  normalise(
    await openedx.patch<Member>(`${base(slug)}/${encodeURIComponent(username)}`, { persona }),
  );

/** Removes tenant access only. The account, and all of its learning, survives. */
export const removeMember = (slug: string, username: string) =>
  openedx.delete<void>(`${base(slug)}/${encodeURIComponent(username)}`);

/* ------------------------------------------------------------------- personas ---- */

/**
 * What each persona means, in the words of the job rather than the role class.
 *
 * `OrgInstructorRole` and `OrgStaffRole` are the primitives underneath, and nobody
 * choosing someone's access should have to know that — or guess whether "instructor"
 * outranks "staff", which is the one thing about Open edX's naming that nobody gets
 * right first time.
 */
export const PERSONA_LABELS: Record<MemberPersona | 'superadmin', string> = {
  tenant_admin: 'Administrator',
  tenant_staff: 'Staff',
  learner: 'Learner',
  superadmin: 'Platform administrator',
};

export const PERSONA_BLURBS: Record<MemberPersona | 'superadmin', string> = {
  tenant_admin:
    'Runs the academy. Everything staff can do, plus adding and removing people, changing what they are, and editing the academy’s settings.',
  tenant_staff:
    'Builds and teaches. Creates courses and content, enrols learners, marks work and sees results — but cannot change who works here.',
  learner:
    'Takes courses. Keeps every enrolment, grade and certificate they already have; only the staff tools go away.',
  superadmin:
    'Operates the whole platform, across every academy. Managed outside this academy — nothing here changes it.',
};

/** Order for pickers and filters: most authority first, so the choice reads as a ladder. */
export const PERSONA_ORDER: MemberPersona[] = ['tenant_admin', 'tenant_staff', 'learner'];

export function personaLabel(persona: string): string {
  return PERSONA_LABELS[persona as MemberPersona] ?? persona;
}

/** Whether this persona runs or teaches the academy, as opposed to learning in it. */
export const isTeam = (persona: Member['persona']): boolean => persona !== 'learner';

/* ---------------------------------------------------------------------- sources ---- */

/**
 * Why someone appears, in words rather than in the name of the table underneath.
 *
 * These are the three independent signals the API unions. Naming them matters more
 * than it looks: a person with no role and no enrolment who merely registered here is a
 * completely different thing from an instructor on one of this academy's courses, and
 * an administrator deciding what to do about a name has to be able to tell which.
 */
export const SOURCE_ORDER: MemberSource[] = ['signup', 'role', 'enrolment'];

export const SOURCE_LABELS: Record<MemberSource, string> = {
  signup: 'Signed up here',
  role: 'Has a role',
  enrolment: 'Enrolled',
};

export const SOURCE_BLURBS: Record<MemberSource, string> = {
  signup: 'They registered on this academy’s own site.',
  role: 'They hold a role here — an academy-wide one, a course team place, or both.',
  enrolment: 'They are taking at least one of this academy’s courses.',
};

export const sourceLabel = (source: string): string =>
  SOURCE_LABELS[source as MemberSource] ?? source;

/**
 * Whether "Remove from academy" has anything to revoke.
 *
 * Removal deletes the tenant membership row and the academy's roles — and nothing else.
 * For someone who appears ONLY because they are enrolled, that is a no-op with a
 * confirmation dialog in front of it: they would still be here afterwards, still
 * enrolled, and the administrator would rightly conclude the button is broken.
 */
export const canRemove = (member: Member): boolean =>
  member.sources.length === 0 ||
  member.sources.includes('signup') ||
  member.sources.includes('role');

/* --------------------------------------------------------------------- errors ---- */

/**
 * The server's own sentence, when it sent one.
 *
 * Every refusal in the member API carries a `detail` written for the person reading it
 * ("X is the only administrator of this academy…"), so showing that beats anything this
 * layer could invent from a status code. `code` is the machine-readable twin, used to
 * pick a heading rather than to write the explanation.
 */
export interface MemberError {
  status: number;
  code: string | null;
  detail: string | null;
}

export function memberError(error: unknown): MemberError {
  if (!(error instanceof ApiError)) {
    return { status: 0, code: null, detail: null };
  }
  const body = (error.body ?? {}) as { detail?: unknown; code?: unknown };
  return {
    status: error.status,
    code: typeof body.code === 'string' ? body.code : null,
    detail: typeof body.detail === 'string' ? body.detail : null,
  };
}
